import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Check, ErrorOutline, Warning } from '@mui/icons-material';
import { useAuth } from '../../../context/AuthContext';

type ConfirmationProps = {
  setActiveStep: (step: number) => void;
};

function Confirmation({ setActiveStep }: ConfirmationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const [redirectSeconds, setRedirectSeconds] = useState(5);

  // Pobieramy parametry z URL
  const params = new URLSearchParams(location.search);
  const paymentStatus = params.get('payment');
  const redirectStatus = params.get('redirect_status');
  const orderId = params.get('orderId');
  const paymentIntent = params.get('payment_intent');

  // Określamy rzeczywisty status płatności - jeśli redirect_status jest 'failed', to priorytetyzujemy ten status
  const actualPaymentStatus =
    redirectStatus === 'failed' ? 'failed' : paymentStatus;

  useEffect(() => {
    setActiveStep(3);
  }, [setActiveStep]);

  useEffect(() => {
    // Rozpocznij odliczanie do przekierowania tylko jeśli mamy orderId
    if (orderId) {
      const timer = setInterval(() => {
        setRedirectSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Przekieruj do odpowiedniej strony w zależności od statusu logowania
            const redirectPath = isAuthenticated
              ? `/profil/zamówienia/${orderId}`
              : `/zamówienie/${orderId}`;
            navigate(redirectPath);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [orderId, navigate, isAuthenticated]);

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.5,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  // Jeśli brak orderId
  if (!orderId) {
    return (
      <motion.div
        className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="text-center bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 p-8"
          variants={itemVariants}
        >
          <ErrorOutline className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 mb-2">
            Nie znaleziono informacji o zamówieniu
          </h2>
          <p className="text-gray-600 mb-6">
            Nie możemy znaleźć informacji o Twoim zamówieniu. Sprawdź swoje
            zamówienia w profilu lub skontaktuj się z obsługą klienta.
          </p>
          <div className="flex justify-center gap-4">
            <motion.button
              className="px-6 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Wróć do sklepu
            </motion.button>
            {isAuthenticated && (
              <motion.button
                className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors"
                onClick={() => navigate('/profil/zamówienia')}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Moje zamówienia
              </motion.button>
            )}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Jeśli płatność się rzeczywiście powiodła (success bez failed redirect_status)
  if (actualPaymentStatus === 'success') {
    return (
      <motion.div
        className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="text-center bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 p-8"
          variants={itemVariants}
        >
          <motion.div
            className="bg-green-50 rounded-full p-4 mx-auto mb-6 inline-flex"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <Check className="h-16 w-16 text-green-500" />
          </motion.div>
          <motion.h2
            className="text-2xl font-medium text-gray-900 mb-2"
            variants={itemVariants}
          >
            Dziękujemy za złożenie zamówienia!
          </motion.h2>
          <motion.p className="text-gray-600 mb-2" variants={itemVariants}>
            Twoja płatność została zrealizowana pomyślnie.
          </motion.p>
          <motion.p
            className="text-primary font-medium text-lg mb-6"
            variants={itemVariants}
          >
            Numer zamówienia: {orderId}
          </motion.p>
          <motion.div
            className="bg-gray-50 p-4 rounded-lg mb-8 mx-auto max-w-lg"
            variants={itemVariants}
          >
            <p className="text-gray-700">
              Potwierdzenie zamówienia zostało wysłane na Twój adres e-mail. Za
              chwilę zostaniesz przekierowany do szczegółów zamówienia.
            </p>
            <div className="mt-3 text-sm text-gray-500">
              Przekierowanie nastąpi za{' '}
              <span className="font-semibold">{redirectSeconds}</span> sekund...
            </div>
            <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
              <div
                className="bg-green-500 h-1.5 rounded-full transition-all duration-1000"
                style={{ width: `${(redirectSeconds / 5) * 100}%` }}
              />
            </div>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-4">
            <motion.button
              className="px-6 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
              onClick={() => {
                const redirectPath = isAuthenticated
                  ? `/profil/zamówienia/${orderId}`
                  : `/zamówienie/${orderId}`;
                navigate(redirectPath);
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              Przejdź do szczegółów zamówienia
            </motion.button>
            <motion.button
              className="px-6 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md text-sm font-medium transition-colors"
              onClick={() => navigate('/')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              Kontynuuj zakupy
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Jeśli jest redirect_status=failed (niezależnie od payment=success)
  if (redirectStatus === 'failed') {
    return (
      <motion.div
        className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.div
          className="text-center bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 p-8"
          variants={itemVariants}
        >
          <ErrorOutline className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-medium text-gray-900 mb-2">
            Płatność nie powiodła się
          </h2>
          <p className="text-gray-600 mb-6">
            Wystąpił problem podczas przetwarzania płatności. Twoje zamówienie
            zostało zapisane, ale nie zostało opłacone.
          </p>
          <motion.div
            className="bg-red-50 p-4 rounded-lg mb-8 mx-auto max-w-lg border border-red-100"
            variants={itemVariants}
          >
            <p className="text-red-800">
              {paymentIntent ? (
                <>
                  Identyfikator płatności:{' '}
                  <span className="font-mono text-xs">{paymentIntent}</span>
                </>
              ) : (
                'Płatność została odrzucona przez system płatności.'
              )}
            </p>
            <div className="mt-3 text-sm text-red-700">
              Przekierowanie nastąpi za{' '}
              <span className="font-semibold">{redirectSeconds}</span> sekund...
            </div>
            <div className="mt-2 w-full bg-red-200 rounded-full h-1.5">
              <div
                className="bg-red-500 h-1.5 rounded-full transition-all duration-1000"
                style={{ width: `${(redirectSeconds / 5) * 100}%` }}
              />
            </div>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-4">
            <motion.button
              className="px-6 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
              onClick={() => {
                const redirectPath = isAuthenticated
                  ? `/profil/zamówienia/${orderId}`
                  : `/zamówienie/${orderId}`;
                navigate(redirectPath);
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              Sprawdź szczegóły zamówienia
            </motion.button>
            <motion.button
              className="px-6 py-2 border border-red-300 bg-red-50 text-red-800 hover:bg-red-100 rounded-md text-sm font-medium transition-colors"
              onClick={() => {
                setActiveStep(2);
                const newPath = location.pathname.replace(
                  'potwierdzenie',
                  'płatność',
                );
                navigate(newPath);
              }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              variants={itemVariants}
            >
              Spróbuj zapłacić ponownie
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    );
  }

  // Domyślnie - jeśli płatność ma nieznany status lub brak statusu
  return (
    <motion.div
      className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="text-center bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 p-8"
        variants={itemVariants}
      >
        <Warning className="h-16 w-16 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-medium text-gray-900 mb-2">
          Status płatności niepewny
        </h2>
        <p className="text-gray-600 mb-6">
          Nie otrzymaliśmy jeszcze potwierdzenia płatności lub wystąpił problem.
          Sprawdź status zamówienia w szczegółach.
        </p>
        <motion.div
          className="bg-amber-50 p-4 rounded-lg mb-8 mx-auto max-w-lg border border-amber-100"
          variants={itemVariants}
        >
          <p className="text-amber-800">
            Jeśli płatność została zrealizowana, środki zostaną zwrócone w ciągu
            3-5 dni roboczych. Możesz spróbować opłacić zamówienie ponownie.
          </p>
          <div className="mt-3 text-sm text-amber-700">
            Przekierowanie nastąpi za{' '}
            <span className="font-semibold">{redirectSeconds}</span> sekund...
          </div>
          <div className="mt-2 w-full bg-amber-200 rounded-full h-1.5">
            <div
              className="bg-amber-500 h-1.5 rounded-full transition-all duration-1000"
              style={{ width: `${(redirectSeconds / 5) * 100}%` }}
            />
          </div>
        </motion.div>
        <div className="flex flex-wrap justify-center gap-4">
          <motion.button
            className="px-6 py-2 bg-primary text-white hover:bg-primary-dark rounded-md text-sm font-medium transition-colors"
            onClick={() => {
              const redirectPath = isAuthenticated
                ? `/profil/zamówienia/${orderId}`
                : `/zamówienie/${orderId}`;
              navigate(redirectPath);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            variants={itemVariants}
          >
            Sprawdź szczegóły zamówienia
          </motion.button>
          <motion.button
            className="px-6 py-2 border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 rounded-md text-sm font-medium transition-colors"
            onClick={() => {
              const newPath = location.pathname.replace(
                'potwierdzenie',
                'płatność',
              );
              navigate(newPath);
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            variants={itemVariants}
          >
            Spróbuj zapłacić ponownie
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default Confirmation;
